import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { TextTooltip } from '@components/atoms/tooltip/prebuilt/text-tooltip/text-tooltip';
import { cn } from '@utils';

const labelVariants = cva('leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70', {
	variants: {
		variant: {
			primary: 'text-primary',
			secondary: 'text-secondary',
			tertiary: 'text-tertiary',
			disabled: 'text-disabled',
			black: 'text-black',
		},
		weight: {
			light: 'font-light',
			medium: 'font-medium',
			bold: 'font-bold',
		},
		size: {
			sm: 'text-sm',
			xs: 'text-xs',
		},
	},
	defaultVariants: {
		variant: 'primary',
		weight: 'medium',
		size: 'sm',
	},
});

type LabelProps = {
	label?: string | React.ReactNode;
	description?: string;
} & React.LabelHTMLAttributes<HTMLLabelElement> &
	VariantProps<typeof labelVariants>;

const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
	({ className, htmlFor, weight, variant, size, label, description, ...props }, ref) => (
		// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<label
			ref={ref}
			className={cn(className, 'mb-0 flex min-w-0 cursor-pointer flex-col')}
			htmlFor={htmlFor}
			onMouseDown={event => {
				// only prevent text selection if clicking inside the label itself
				const target = event.target as HTMLElement;
				if (target.closest('button, input, select, textarea')) return;

				props.onMouseDown?.(event);
				// prevent text selection when double clicking label
				if (!event.defaultPrevented && event.detail > 1) event.preventDefault();
			}}
			{...props}
		>
			<span
				className={cn(
					labelVariants({
						weight,
						variant,
						size,
					})
				)}
			>
				{label}
			</span>
			{description && (
				<TextTooltip color="secondary" size="xs" weight="medium" truncate>
					{description}
				</TextTooltip>
			)}
		</label>
	)
);
Label.displayName = 'Label';

export default Label;

export { LabelProps };
